import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { NgRecaptcha3Service } from 'ng-recaptcha3';
import { CardSetTransformationService, CoreService, DataService, DialogResult, DialogService, FormNavigationService, SpinnerService, VerticalNavigationSettings } from 'dspx-core';
import { BaseCardSetDetailComponent } from '../base-card-set-detail-component/base-card-set-detail.component';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { ErrorService } from '../../services/error.service';
import { AppDataService } from '../../services/app-data.service';
import { FormDataService, HideableGrid } from '../../services/form-data.service';
import { QuestionPath, ResponseParserService } from '../../services/response-parser.service';
import { HtmlHelperService } from '../../services/html-helper.service';
import { JsonSerialisationService } from '../../services/json-serialisation.service';
import { TypeaheadQuestionModel } from '../../interfaces/typeahead-question-model.interface';
import { EntraIdUser } from '../../interfaces/entra-id-user.interface';
import { DopiFormSaveRequest } from '../../interfaces/dopi-form-save-request.interface';
import { API_ENDPOINT_KEYS, API_ENDPOINTS } from '../../constants/api-endpoints.constants';
import { TEMPLATES, VERSIONS } from '../../constants/templates.constants';
import { APP_DATA_KEYS, FORM_ACTIONS, HIDEABLE_GRIDS, NAV_TAB_NUMBERS, PAGE_NUMBERS, POP_UP_CODES, QUESTION_CODES, READ_ONLY_ACCESS_PAGE_ACTION_BUTTONS, SECTION_CODES, SERVER_ERROR_POP_UP_CODES } from '../../constants/app-data.constants';
import { ROUTE_CONSTANTS } from '../../constants/route.constants';
import { take } from 'rxjs';

@Component({
  selector: 'app-dopi-form',
  templateUrl: './dopi-form.component.html',
  styleUrl: './dopi-form.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class DopiFormComponent extends BaseCardSetDetailComponent implements OnInit, OnDestroy {

  fieldTypeReplacements = CardSetTransformationService.cardSetWithFlatTabsFieldTypeReplacements;
  verticalNavigationSettings: VerticalNavigationSettings = { enabled: true, sticky: true, pageLevel: true };
  id = '';
  formServiceEndpoint: string = API_ENDPOINT_KEYS.getDopiFormConfigWithEmptyResponse;
  templateId: number = TEMPLATES.dopiForm;
  versionNo: number = VERSIONS.latest;
  activeDialog: string;
  editing = false;
  isExternalRoute = false;
  isViewRoute = false;

  private versionHistoryHideableGrid: HideableGrid;
  private approver: EntraIdUser;

  constructor(
    protected router: Router,
    protected appDataService: AppDataService,
    protected coreService: CoreService,
    protected formDataService: FormDataService,
    protected htmlHelperService: HtmlHelperService,
    protected dialogService: DialogService,
    private route: ActivatedRoute,
    private recaptchaService: NgRecaptcha3Service,
    private errorService: ErrorService,
    private dataService: DataService,
    private spinnerService: SpinnerService,
    private responseParserService: ResponseParserService,
    private jsonSerialisationService: JsonSerialisationService,
    private formNavigationService: FormNavigationService) {
    super(router, appDataService, coreService, formDataService, htmlHelperService, dialogService);
  }

  ngOnDestroy(): void {
    this.unsubscribeSubscriptions();
    this.coreService.applicationFunctions = {};
    this.coreService.repeatingPageFunctions = {};
    this.coreService.repeatingSectionFunctions = {};
  }

  ngOnInit(): void {
    this.registerSubscription(
      this.route.paramMap.subscribe((paramMap: ParamMap) => {
        const id = paramMap.get('id');
        if (id) {
          this.id = id;
          this.isExternalRoute = this.router.url === `/${ROUTE_CONSTANTS.dopiForm.externalPrefix}/${id}` || this.router.url === `/${ROUTE_CONSTANTS.dopiForm.externalViewPrefix}/${id}`;
          this.isViewRoute = this.router.url === `/${ROUTE_CONSTANTS.dopiForm.viewPrefix}/${id}` || this.router.url === `/${ROUTE_CONSTANTS.dopiForm.externalViewPrefix}/${id}`;
          this.formServiceEndpoint = this.isExternalRoute
            ? this.isViewRoute
              ? API_ENDPOINT_KEYS.getDopiFormConfigAndResponseExternalReadOnly
              : API_ENDPOINT_KEYS.getDopiFormConfigAndResponseExternal
            : this.isViewRoute
              ? API_ENDPOINT_KEYS.getDopiFormConfigAndResponseReadOnly
              : API_ENDPOINT_KEYS.getDopiFormConfigAndResponse;
          this.editing = true;
        }
        else {
          this.resetDirtyFlagAfterLoading();
        }
      })
    );

    this.formNavigationService.onNavigation().subscribe(event => {
      setTimeout(() => {
        this.overrideHtmlTitleSetByDspxFramework();
      })
    });

    this.coreService.conditionFunctions['isEditing'] = () => this.editing;
    this.coreService.conditionFunctions['isReadOnly'] = () => true;

    this.coreService.applicationFunctions['canSave'] = () => this.isFormEditable;
    this.coreService.applicationFunctions['downloadCopy'] = () => this.downloadCopy();
    this.coreService.applicationFunctions['canDelete'] = () => this.isViewRoute;
    this.coreService.applicationFunctions['delete'] = () => this.delete(null);
    this.coreService.applicationFunctions['canSwitchToEdit'] = () => this.isViewRoute;
    this.coreService.applicationFunctions['switchToEdit'] = () => this.switchToEdit(null);

    this.versionHistoryHideableGrid = this.initialiseHideableGrid(HIDEABLE_GRIDS.dopiForm.versionHistory);
  }

  formLoaded(event: any): void {
    if (event.retryAsReadOnly) {
      const viewPrefix = this.isExternalRoute
        ? ROUTE_CONSTANTS.dopiForm.externalViewPrefix
        : ROUTE_CONSTANTS.dopiForm.viewPrefix;
      this.navigate(['/', viewPrefix, this.id], true);
      return;
    }

    if (this.isRecaptchaRequired()) {
      const recaptchaSiteKey = this.appDataService.getAppData(APP_DATA_KEYS.googleRecaptchaSiteKey);
      this.recaptchaService.init(recaptchaSiteKey).then(status => {
        console.log('reCAPTCHA service init: ' + status);
      });
    }

    this.isFormEditable = !!event?.isFormEditable;

    if (this.baseForm && this.baseForm.model) {
      this.appDataService.setAppData(APP_DATA_KEYS.previousModel, JSON.parse(JSON.stringify(this.baseForm.model)));
    }

    this.versionHistoryHideableGrid.toggle(HIDEABLE_GRIDS.dopiForm.versionHistory.showOnFirstLoad);

    this.ensurePageActionButtonsAreEnabledOnFormLoad(READ_ONLY_ACCESS_PAGE_ACTION_BUTTONS.dopiForm);
    this.handlePostFormLoadActions(event);
  }

  modelChanged(event: any): void {
    this.handleSingleCheckBoxChanges();
    this.handleApproverChange();
  }

  switchToEdit(event: any): void {
    this.navigate(['/', ROUTE_CONSTANTS.dopiForm.prefix, this.id], true);
  }

  delete(event: any): void {
    this.registerSubscription(
      this.dialogService
        .showConfirm(POP_UP_CODES.dopiForm.deleteConfirmation)
        .pipe(take(1))
        .subscribe(data => {
          switch (data as DialogResult) {
            case DialogResult.ACCEPTED: {
              this.spinnerService.showSpinner();
              this.registerSubscription(
                this.appDataService.delete(`${API_ENDPOINTS.dopiForm.delete}/${this.id}`)
                  .pipe(take(1), this.errorService.errorHandler())
                  .subscribe(data => {
                    this.spinnerService.hideSpinner();
                    const isSuccess = !SERVER_ERROR_POP_UP_CODES.deleteDopiForm.includes(data.message);
                    this.activeDialog = isSuccess ? POP_UP_CODES.dopiForm.deleteSuccessful : data.message;
                    this.dialogService.showDialog(this.activeDialog);
                    if (isSuccess) {
                      this.navigateBackToDashboard();
                    }
                  })
              );
              break;
            }
            case DialogResult.REJECTED: {
              break;
            }
          }
        })
    );
  }

  save(event: any) {

    const saveData = (request: DopiFormSaveRequest) => {
      if (this.isExternalRoute && !request.isSubmit) {
        this.registerSubscription(
          this.dialogService
            .showConfirm(POP_UP_CODES.dopiForm.externalSaveConfirmation)
            .pipe(take(1))
            .subscribe(data => {
              switch (data as DialogResult) {
                case DialogResult.ACCEPTED: {
                  this.saveData(request);
                  break;
                }
                case DialogResult.REJECTED: {
                  break;
                }
              }
            })
        );
      }
      else {
        this.saveData(request);
      }
    }

    const saveDataRequest: DopiFormSaveRequest = {
      template: this.templateId,
      versionNumber: this.versionNo,
      id: this.id,
      response: event.model,
      expectedLastUpdateTime: event.lastUpdatedTime,
      isSubmit: event.isSubmit,
      recaptchaToken: null // to be set later
    };

    if (!event.isFormValid && event.isSubmit) {
      this.baseForm.markAllFieldsAsTouched();
      if (!this.baseForm.fields[0]?.formControl.valid) {
        this.baseForm.showValidationSummary(true);
        this.dialogService.showDialog(POP_UP_CODES.common.validationError);
      }
      else {
        saveData(saveDataRequest);
      }
    }
    else {
      saveData(saveDataRequest);
    }
  }

  cancel(event: any) {
    this.navigateBackToDashboard();
  }

  dialogSubmitted(event: any) {

    const activeDialog = this.activeDialog;
    delete this.activeDialog;

    switch (activeDialog) {
      case POP_UP_CODES.common.saveSuccessful: {
        this.navigateBackToDashboard(true);
        break;
      }
      case POP_UP_CODES.common.submitSuccessful: {
        if (event?.downloadCopy) {
          this.downloadCopy();
        }
        this.navigateBackToDashboard(true);
        break;
      }
    }
  }

  actionButtonClicked(event: any): void {
    switch (event.field.key) {
      case QUESTION_CODES.dopiForm.versionHistory: {
        switch (event.event.type) {
          case FORM_ACTIONS.dopiForm.downloadVersion: {
            this.downloadVersion(event.event.data.id);
            break;
          }
        }
        break;
      }
    }
  }

  private isRecaptchaRequired(): boolean {
    return this.isExternalRoute;
  }

  private navigateBackToDashboard(ignoreUnsavedChanges = false): void {
    if (this.isExternalRoute) {
      this.navigateToEndOfSessionPage(ignoreUnsavedChanges);
    }
    else {
      this.navigate(['/', ROUTE_CONSTANTS.dopiForm.list], ignoreUnsavedChanges);
    }
  }

  private saveData(request: DopiFormSaveRequest) {

    const saveDataFunction = (request: DopiFormSaveRequest) => {
      this.spinnerService.showSpinner();
      const saveEndpoint = this.isExternalRoute
        ? API_ENDPOINTS.dopiForm.saveExternal
        : API_ENDPOINTS.dopiForm.save;
      this.registerSubscription(
        this.dataService.saveData(saveEndpoint, request)
          .pipe(take(1), this.errorService.errorHandler())
          .subscribe(data => {
            this.spinnerService.hideSpinner();
            if (SERVER_ERROR_POP_UP_CODES.saveDopiForm.includes(data.message)) {
              this.activeDialog = data.message;
            }
            else {
              this.id = data.id;
              this.activeDialog = request.isSubmit
                ? POP_UP_CODES.common.submitSuccessful
                : POP_UP_CODES.common.saveSuccessful;
            }
            this.dialogService.showDialog(this.activeDialog);
          })
      );
    };

    if (this.isRecaptchaRequired()) {
      this.recaptchaService.getToken().then(
        token => {
          request.recaptchaToken = token;
          saveDataFunction(request);
        },
        error => console.log('reCAPTCHA service getToken error: ' + error));
    }
    else {
      saveDataFunction(request);
    }
  }

  private downloadCopy(): void {
    const downloadCopyEndpoint = this.isExternalRoute
      ? `${API_ENDPOINTS.dopiForm.downloadCopyExternal}/${this.id}`
      : `${API_ENDPOINTS.dopiForm.downloadCopy}/${this.id}`;
    this.appDataService.generatePdf(downloadCopyEndpoint);
  }

  private downloadVersion(versionId: string): void {
    const downloadVersionEndpoint = this.isExternalRoute
      ? `${API_ENDPOINTS.dopiForm.downloadVersionExternal}/${this.id}/${versionId}`
      : `${API_ENDPOINTS.dopiForm.downloadVersion}/${this.id}/${versionId}`;
    this.appDataService.generatePdf(downloadVersionEndpoint);
  }

  private handleSingleCheckBoxChanges(): void {
    const consentQuestionPath: QuestionPath = [
      NAV_TAB_NUMBERS.dopiForm.attestation,
      PAGE_NUMBERS.dopiForm.attestation,
      SECTION_CODES.dopiForm.attestationSection,
      QUESTION_CODES.dopiForm.consent
    ];
    this.responseParserService.handleSingleCheckBoxQuestionChanges(
      () => this.baseForm.model,
      model => this.baseForm.model = model,
      [consentQuestionPath]);
  }

  private handleApproverChange(): void {
    const approver = this.getApprover();
    if (this.approver?.email !== approver?.email) {
      this.approver = approver;
      this.setApproverRole(approver?.role);
      this.reloadModel();
    }
  }

  private getApprover(): EntraIdUser {
    const approverQuestionModel: TypeaheadQuestionModel<string> = this.responseParserService.findQuestionModel(
      this.baseForm.model,
      NAV_TAB_NUMBERS.dopiForm.yourApprover,
      PAGE_NUMBERS.dopiForm.yourApprover,
      null,
      SECTION_CODES.dopiForm.yourApproverSection,
      null,
      QUESTION_CODES.dopiForm.yourApprover);
    const approver = this.jsonSerialisationService.tryDeserialise<EntraIdUser>(approverQuestionModel?.code);
    return approver;
  }

  private setApproverRole(approveRole: string): void {
    this.responseParserService.replaceQuestionModel(
      this.baseForm.model,
      NAV_TAB_NUMBERS.dopiForm.yourApprover,
      PAGE_NUMBERS.dopiForm.yourApprover,
      null,
      SECTION_CODES.dopiForm.yourApproverSection,
      null,
      QUESTION_CODES.dopiForm.approverRole,
      approveRole);
  }
}
